import { createFileRoute } from "@tanstack/react-router";
import { ArticlePage } from "@/components/ArticlePage";
import { articleSchemas } from "@/lib/seo";

const SITE = "https://shopify-freelancer.net";
const PATH = "/ratgeber/shopify-kosten";
const TITLE = "Shopify Kosten 2026: Pläne, Gebühren, Apps im Überblick";
const DESCRIPTION =
  "Was Shopify wirklich kostet: Monatspläne, Transaktionsgebühren, Apps, Theme und Umsetzung, mit Beispielrechnung für einen Shop mit 50.000 € Monatsumsatz.";

const faqs = [
  {
    q: "Was kostet Shopify im Monat?",
    a: "Die gängigen Pläne liegen bei rund 27 € (Basic), 79 € (Grow/Shopify) und 289 € (Advanced) pro Monat bei jährlicher Zahlung. Shopify Plus beginnt im Bereich von etwa 2.300 € monatlich.",
  },
  {
    q: "Welche Gebühren kommen zusätzlich zum Plan dazu?",
    a: "Zahlungsgebühren von etwa 1,3 bis 2,3 Prozent plus 25 Cent pro Transaktion, bei externen Zahlungsanbietern zusätzlich 0,2 bis 2 Prozent Shopify-Transaktionsgebühr. Dazu kommen Apps, Theme und gegebenenfalls Umsetzung.",
  },
  {
    q: "Wie viel kosten Shopify-Apps?",
    a: "Typische Shops zahlen 50 bis 300 € pro Monat für Apps. Viele davon lassen sich durch Theme-Funktionen ersetzen, was Kosten spart und die Ladezeit verbessert.",
  },
  {
    q: "Welcher Shopify-Plan lohnt sich ab wann?",
    a: "Basic reicht bis etwa 20.000 € Monatsumsatz. Darüber rechnet sich der mittlere Plan meist allein über die niedrigeren Zahlungsgebühren. Advanced lohnt sich in der Regel ab rund 100.000 € Monatsumsatz.",
  },
  {
    q: "Was ist der größte versteckte Kostenblock?",
    a: "Nicht der Plan, sondern die verlorene Conversion. Ein Prozentpunkt weniger Kaufabschlussrate kostet bei 50.000 Sessions und 70 € Warenkorb rund 35.000 € Umsatz im Monat, ein Vielfaches aller Plangebühren.",
  },
];

export const Route = createFileRoute("/ratgeber/shopify-kosten")({
  component: Page,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}${PATH}` },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: articleSchemas({ title: TITLE, description: DESCRIPTION, path: PATH, faqs }),
  }),
});

function Page() {
  return (
    <ArticlePage
      kicker="Ratgeber · Kosten"
      title="Shopify Kosten: was der Shop wirklich pro Monat frisst"
      intro="Die Planpreise sind der kleinste Teil der Rechnung. Entscheidend sind Zahlungsgebühren, Apps und der Umsatz, der an schlechten Kaufabschlussraten verloren geht. Diese Übersicht rechnet alles zusammen."
      source="ratgeber-shopify-kosten"
      sections={[
        {
          h: "Die Planpreise im Überblick",
          p: [
            "Preise gelten bei jährlicher Zahlung und können je nach Markt leicht abweichen. Monatliche Zahlung ist rund 25 Prozent teurer.",
          ],
          list: [
            "Basic: ca. 27 € pro Monat, 2,0 % Kartengebühr im Onlineverkauf",
            "Grow / Shopify: ca. 79 € pro Monat, günstigere Kartengebühr",
            "Advanced: ca. 289 € pro Monat, beste Konditionen im Standardbereich",
            "Plus: ab ca. 2.300 € pro Monat, für Checkout-Erweiterungen und hohe Volumen",
          ],
        },
        {
          h: "Transaktions- und Zahlungsgebühren",
          p: [
            "Zwei Gebühren werden häufig verwechselt: Die Kartengebühr zahlen Sie an den Zahlungsanbieter, die Transaktionsgebühr zusätzlich an Shopify, wenn Sie nicht Shopify Payments nutzen. Diese Zusatzgebühr liegt je nach Plan bei 0,2 bis 2 Prozent.",
            "Bei 50.000 € Monatsumsatz sind das schnell 1.000 € Unterschied, allein durch die Wahl des Zahlungs-Setups.",
          ],
        },
        {
          h: "Apps: der unterschätzte Posten",
          p: [
            "Die meisten Shops, die ich sehe, zahlen zwischen 50 und 300 € monatlich für Apps. Der teurere Teil ist unsichtbar: Jede App lädt Skripte, verlangsamt den Seitenaufbau und kostet damit Kaufabschlüsse.",
            "Eine App-Inventur zu Beginn eines Projekts spart regelmäßig 100 € im Monat und eine halbe Sekunde Ladezeit.",
          ],
        },
        {
          h: "Beispielrechnung für einen Shop mit 50.000 € Umsatz",
          p: [
            "Plan 79 €, Zahlungsgebühren rund 1.000 €, Apps 150 €, Theme-Abschreibung 30 €, zusammen etwa 1.260 € pro Monat oder 2,5 Prozent vom Umsatz.",
            "Dem gegenüber steht der Effekt einer Verbesserung der Kaufabschlussrate von 1,4 auf 2,0 Prozent: rund 21.000 € Mehrumsatz im Monat bei identischem Traffic. Genau in dieser Relation sollte über Budgets entschieden werden.",
          ],
        },
        {
          h: "Wo Sie sinnvoll sparen, und wo nicht",
          p: [
            "Sparen lohnt sich bei Apps, bei doppelten Tools und bei Zahlungs-Setups. Nicht sparen sollten Sie bei Produktbildern, Ladezeit und der mobilen Produktseite: Das sind die drei Stellen, an denen der Umsatz entsteht.",
          ],
        },
      ]}
      faqs={faqs}
      related={[
        { to: "/fragen/was-kostet-ein-shopify-shop", label: "Kurzantwort: Was kostet ein Shopify-Shop?" },
        { to: "/ratgeber/shopify-agentur-kosten", label: "Agentur vs. Freelancer" },
        { to: "/roi-rechner", label: "Conversion-Rechner" },
        { to: "/preise", label: "Meine Preise" },
      ]}
    />
  );
}
